import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { AdminUser } from '../lib/authService'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Megaphone,
  Clock,
  Pin,
  PinOff,
  Plus,
  Loader2,
  MoreVertical,
  Pencil,
  Trash2,
  AlertTriangle,
  AlertCircle,
  Info,
  Bell,
  Calendar,
  User,
  ImagePlus,
  X,
} from 'lucide-react'

interface Announcement {
  id: string
  church_id: string
  author_id: string | null
  title: string
  content: string
  priority: 'low' | 'medium' | 'high' | 'urgent'
  target_groups: string[] | null
  target_roles: string[] | null
  start_date: string | null
  end_date: string | null
  is_pinned: boolean
  attachments: any
  created_at: string
  updated_at: string
  profiles?: {
    first_name: string
    last_name: string
    photo_url: string | null
  }
}

interface AnnouncementsProps {
  user: AdminUser | null
}

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', color: 'bg-gray-500', icon: Info },
  { value: 'medium', label: 'Medium', color: 'bg-blue-500', icon: Bell },
  { value: 'high', label: 'High', color: 'bg-orange-500', icon: AlertCircle },
  { value: 'urgent', label: 'Urgent', color: 'bg-red-500', icon: AlertTriangle },
]

export default function Announcements({ user: currentUser }: AnnouncementsProps) {
  const [announcements, setAnnouncements] = useState<Announcement[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'active' | 'all' | 'pinned'>('active')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null)
  const [saving, setSaving] = useState(false)

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
    is_pinned: false,
    start_date: '',
    end_date: '',
    image_url: '',
  })
  const [uploadingImage, setUploadingImage] = useState(false)

  useEffect(() => {
    loadAnnouncements()
  }, [currentUser])

  const loadAnnouncements = async () => {
    if (!currentUser?.churchId) return

    console.log('📢 [Admin Announcements] Loading for church:', currentUser.churchId)

    try {
      setLoading(true)

      const { data, error } = await supabase
        .from('announcements')
        .select(`
          *,
          profiles:author_id (
            first_name,
            last_name,
            photo_url
          )
        `)
        .eq('church_id', currentUser.churchId)
        .order('is_pinned', { ascending: false })
        .order('created_at', { ascending: false })

      if (error) {
        console.error('📢 [Admin Announcements] Error:', error)
        throw error
      }

      console.log('📢 [Admin Announcements] Found', data?.length || 0, 'announcements')
      data?.forEach((a, i) => console.log(`   ${i + 1}. "${a.title}" (id: ${a.id})`))

      setAnnouncements(data || [])
    } catch (error) {
      console.error('Error loading announcements:', error)
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      priority: 'medium',
      is_pinned: false,
      start_date: '',
      end_date: '',
      image_url: '',
    })
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file')
      return
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB')
      return
    }

    try {
      setUploadingImage(true)

      // Generate unique filename
      const fileExt = file.name.split('.').pop()
      const fileName = `announcement-${Date.now()}.${fileExt}`
      const filePath = `announcements/${currentUser?.churchId}/${fileName}`

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('media')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        })

      if (error) {
        console.error('Upload error:', error)
        throw error
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('media')
        .getPublicUrl(filePath)

      setFormData({ ...formData, image_url: publicUrl })
    } catch (error: any) {
      console.error('Error uploading image:', error)
      alert(`Failed to upload image: ${error.message || 'Unknown error'}`)
    } finally {
      setUploadingImage(false)
    }
  }

  const removeImage = () => {
    setFormData({ ...formData, image_url: '' })
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.title.trim()) {
      alert('Please enter a title')
      return
    }

    if (!formData.content.trim()) {
      alert('Please enter content')
      return
    }

    try {
      setSaving(true)

      console.log('📢 [Admin] Creating announcement:', {
        church_id: currentUser?.churchId,
        author_id: currentUser?.id,
        title: formData.title.trim(),
      })

      const { data, error } = await supabase
        .from('announcements')
        .insert({
          church_id: currentUser?.churchId,
          author_id: currentUser?.id,
          title: formData.title.trim(),
          content: formData.content.trim(),
          priority: formData.priority,
          is_pinned: formData.is_pinned,
          start_date: formData.start_date || null,
          end_date: formData.end_date || null,
          attachments: formData.image_url ? { image: formData.image_url } : null,
        })
        .select()

      if (error) {
        console.error('📢 [Admin] Create error:', error)
        throw error
      }

      console.log('📢 [Admin] Created successfully:', data)

      setShowCreateModal(false)
      resetForm()
      await loadAnnouncements()
    } catch (error: any) {
      console.error('Error creating announcement:', error)
      alert(`Failed to create announcement: ${error.message || 'Unknown error'}`)
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedAnnouncement) return

    if (!formData.title.trim()) {
      alert('Please enter a title')
      return
    }

    if (!formData.content.trim()) {
      alert('Please enter content')
      return
    }

    try {
      setSaving(true)

      const { error } = await supabase
        .from('announcements')
        .update({
          title: formData.title.trim(),
          content: formData.content.trim(),
          priority: formData.priority,
          is_pinned: formData.is_pinned,
          start_date: formData.start_date || null,
          end_date: formData.end_date || null,
          attachments: formData.image_url ? { image: formData.image_url } : null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', selectedAnnouncement.id)

      if (error) throw error

      setShowEditModal(false)
      setSelectedAnnouncement(null)
      resetForm()
      await loadAnnouncements()
    } catch (error) {
      console.error('Error updating announcement:', error)
      alert('Failed to update announcement')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedAnnouncement) return

    try {
      setSaving(true)

      const { error } = await supabase
        .from('announcements')
        .delete()
        .eq('id', selectedAnnouncement.id)

      if (error) throw error

      setShowDeleteDialog(false)
      setSelectedAnnouncement(null)
      await loadAnnouncements()
    } catch (error) {
      console.error('Error deleting announcement:', error)
      alert('Failed to delete announcement')
    } finally {
      setSaving(false)
    }
  }

  const handleTogglePin = async (announcement: Announcement) => {
    try {
      const { error } = await supabase
        .from('announcements')
        .update({
          is_pinned: !announcement.is_pinned,
          updated_at: new Date().toISOString(),
        })
        .eq('id', announcement.id)

      if (error) throw error

      await loadAnnouncements()
    } catch (error) {
      console.error('Error toggling pin:', error)
      alert('Failed to update announcement')
    }
  }

  const openEditModal = (announcement: Announcement) => {
    setSelectedAnnouncement(announcement)
    setFormData({
      title: announcement.title,
      content: announcement.content,
      priority: announcement.priority,
      is_pinned: announcement.is_pinned,
      start_date: announcement.start_date ? announcement.start_date.split('T')[0] : '',
      end_date: announcement.end_date ? announcement.end_date.split('T')[0] : '',
      image_url: announcement.attachments?.image || '',
    })
    setShowEditModal(true)
  }

  const openDeleteDialog = (announcement: Announcement) => {
    setSelectedAnnouncement(announcement)
    setShowDeleteDialog(true)
  }

  const isAnnouncementActive = (announcement: Announcement) => {
    const now = new Date()
    const startDate = announcement.start_date ? new Date(announcement.start_date) : null
    const endDate = announcement.end_date ? new Date(announcement.end_date) : null

    if (startDate && now < startDate) return false
    if (endDate && now > endDate) return false
    return true
  }

  const getFilteredAnnouncements = () => {
    switch (activeTab) {
      case 'active':
        return announcements.filter(isAnnouncementActive)
      case 'pinned':
        return announcements.filter(a => a.is_pinned)
      case 'all':
      default:
        return announcements
    }
  }

  const getPriorityConfig = (priority: string) => {
    return PRIORITY_OPTIONS.find(p => p.value === priority) || PRIORITY_OPTIONS[1]
  }

  const filteredAnnouncements = getFilteredAnnouncements()

  const renderAnnouncementCard = (announcement: Announcement) => {
    const priorityConfig = getPriorityConfig(announcement.priority)
    const PriorityIcon = priorityConfig.icon
    const isActive = isAnnouncementActive(announcement)
    const authorName = announcement.profiles
      ? `${announcement.profiles.first_name} ${announcement.profiles.last_name}`
      : 'Unknown'

    return (
      <Card key={announcement.id} className={`hover:shadow-md transition-shadow ${!isActive ? 'opacity-60' : ''}`}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3 flex-1">
              <div className={`p-2 rounded-lg ${priorityConfig.color} text-white`}>
                <PriorityIcon className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <CardTitle className="text-lg">{announcement.title}</CardTitle>
                  {announcement.is_pinned && (
                    <Badge variant="secondary" className="flex items-center gap-1">
                      <Pin className="h-3 w-3" />
                      Pinned
                    </Badge>
                  )}
                  {!isActive && (
                    <Badge variant="outline" className="text-muted-foreground">
                      Inactive
                    </Badge>
                  )}
                </div>
                <CardDescription className="mt-1 flex items-center gap-2 flex-wrap">
                  <span className="flex items-center gap-1">
                    <User className="h-3 w-3" />
                    {authorName}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {new Date(announcement.created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </span>
                  <Badge variant="outline" className="capitalize">
                    {announcement.priority}
                  </Badge>
                </CardDescription>
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleTogglePin(announcement)}>
                  {announcement.is_pinned ? (
                    <>
                      <PinOff className="mr-2 h-4 w-4" />
                      Unpin
                    </>
                  ) : (
                    <>
                      <Pin className="mr-2 h-4 w-4" />
                      Pin
                    </>
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => openEditModal(announcement)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => openDeleteDialog(announcement)}
                  className="text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>
        <CardContent>
          {/* Display image if available */}
          {announcement.attachments?.image && (
            <div className="mb-4 -mx-6 -mt-2">
              <img
                src={announcement.attachments.image}
                alt={announcement.title}
                className="w-full h-48 object-cover"
              />
            </div>
          )}

          <p className="text-muted-foreground whitespace-pre-wrap">{announcement.content}</p>

          {(announcement.start_date || announcement.end_date) && (
            <div className="mt-4 flex items-center gap-4 text-sm text-muted-foreground">
              <Calendar className="h-4 w-4" />
              {announcement.start_date && (
                <span>
                  From: {new Date(announcement.start_date).toLocaleDateString()}
                </span>
              )}
              {announcement.end_date && (
                <span>
                  Until: {new Date(announcement.end_date).toLocaleDateString()}
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    )
  }

  const renderForm = (isEdit: boolean = false) => (
    <form onSubmit={isEdit ? handleEdit : handleCreate}>
      <div className="space-y-4 py-4">
        {/* Title */}
        <div className="space-y-2">
          <Label htmlFor="title">Title *</Label>
          <Input
            id="title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="Announcement title..."
            required
          />
        </div>

        {/* Content */}
        <div className="space-y-2">
          <Label htmlFor="content">Content *</Label>
          <Textarea
            id="content"
            value={formData.content}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            placeholder="Announcement content..."
            rows={6}
            required
          />
        </div>

        {/* Priority */}
        <div className="space-y-2">
          <Label htmlFor="priority">Priority</Label>
          <Select
            value={formData.priority}
            onValueChange={(value: 'low' | 'medium' | 'high' | 'urgent') =>
              setFormData({ ...formData, priority: value })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRIORITY_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${option.color}`} />
                    {option.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Pin Toggle */}
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label>Pin Announcement</Label>
            <p className="text-sm text-muted-foreground">
              Pinned announcements appear at the top
            </p>
          </div>
          <Switch
            checked={formData.is_pinned}
            onCheckedChange={(checked) => setFormData({ ...formData, is_pinned: checked })}
          />
        </div>

        {/* Date Range */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="start_date">Start Date (optional)</Label>
            <Input
              id="start_date"
              type="date"
              value={formData.start_date}
              onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="end_date">End Date (optional)</Label>
            <Input
              id="end_date"
              type="date"
              value={formData.end_date}
              onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
            />
          </div>
        </div>

        {/* Image Upload */}
        <div className="space-y-2">
          <Label>Cover Image (optional)</Label>
          {formData.image_url ? (
            <div className="relative">
              <img
                src={formData.image_url}
                alt="Announcement preview"
                className="w-full h-48 object-cover rounded-lg"
              />
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="absolute top-2 right-2"
                onClick={removeImage}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <label className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 cursor-pointer hover:border-muted-foreground/50 transition-colors block">
              <div className="flex flex-col items-center justify-center gap-2">
                {uploadingImage ? (
                  <>
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">Uploading...</p>
                  </>
                ) : (
                  <>
                    <ImagePlus className="h-8 w-8 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      Click to upload an image
                    </p>
                    <p className="text-xs text-muted-foreground">
                      PNG, JPG up to 5MB
                    </p>
                  </>
                )}
              </div>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
                disabled={uploadingImage}
              />
            </label>
          )}
        </div>
      </div>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            isEdit ? setShowEditModal(false) : setShowCreateModal(false)
            setSelectedAnnouncement(null)
            resetForm()
          }}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={saving}>
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {isEdit ? 'Saving...' : 'Creating...'}
            </>
          ) : (
            isEdit ? 'Save Changes' : 'Create Announcement'
          )}
        </Button>
      </DialogFooter>
    </form>
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  const activeCount = announcements.filter(isAnnouncementActive).length
  const pinnedCount = announcements.filter(a => a.is_pinned).length

  return (
    <div className="flex flex-col gap-4 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Announcements</h1>
          <p className="text-muted-foreground">
            Create and manage church announcements
          </p>
        </div>
        <Button onClick={() => {
          resetForm()
          setShowCreateModal(true)
        }}>
          <Plus className="mr-2 h-4 w-4" />
          New Announcement
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Announcements</CardTitle>
            <Megaphone className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{announcements.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active</CardTitle>
            <Bell className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pinned</CardTitle>
            <Pin className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pinnedCount}</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'active' | 'all' | 'pinned')}>
        <TabsList>
          <TabsTrigger value="active">
            Active ({activeCount})
          </TabsTrigger>
          <TabsTrigger value="pinned">
            Pinned ({pinnedCount})
          </TabsTrigger>
          <TabsTrigger value="all">
            All ({announcements.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="space-y-4 mt-4">
          {filteredAnnouncements.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Megaphone className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-lg font-medium">No announcements found</p>
                <p className="text-sm text-muted-foreground mb-4">
                  {activeTab === 'active'
                    ? 'Create an announcement to share with your congregation'
                    : activeTab === 'pinned'
                    ? 'No pinned announcements'
                    : 'No announcements yet'}
                </p>
                <Button onClick={() => {
                  resetForm()
                  setShowCreateModal(true)
                }}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create Announcement
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {filteredAnnouncements.map(renderAnnouncementCard)}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-900">
          <DialogHeader>
            <DialogTitle>New Announcement</DialogTitle>
            <DialogDescription>
              Create a new announcement to share with your congregation
            </DialogDescription>
          </DialogHeader>
          {renderForm(false)}
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-900">
          <DialogHeader>
            <DialogTitle>Edit Announcement</DialogTitle>
            <DialogDescription>
              Update the announcement details
            </DialogDescription>
          </DialogHeader>
          {renderForm(true)}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Announcement</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedAnnouncement?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={saving}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
